/**
 * TodayView.jsx - Real-Time BI Dashboard with date selection
 * Default and only dashboard view. Sections: KPIs, Conversion, Exec/City tables,
 * Package Breakdown, Hourly Distribution, Meta Ads Summary, Ad Performance Analytics,
 * Status Aging, Marketing ROI by City
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { dashboardApi, metaAdsApi } from "@/services/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell, ComposedChart, Line,
} from "recharts";
import {
  IndianRupee, TrendingUp, Users, Target, RefreshCw, Loader2,
  UserCheck, Clock, ArrowLeft, BarChart3, MapPin, Brain,
  Activity, Eye, X, Package, Megaphone, Hourglass, Calendar,
  ExternalLink, Info, Key, Shield, Link2,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ConversionIntelligence from "./ConversionIntelligence";

const fmt = (n) => (n || 0).toLocaleString("en-IN");
const fmtK = (n) => {
  if (!n) return "₹0";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
};
// Use LOCAL date (not UTC) to match IST timezone used by the backend
const toISO = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const todayStr = () => toISO(new Date());

// Date range helpers
function getPresetRange(key) {
  const now = new Date();
  const today = todayStr();
  switch (key) {
    case "today": return { start: today, end: today, label: "Today" };
    case "yesterday": { const d = new Date(); d.setDate(d.getDate() - 1); const y = toISO(d); return { start: y, end: y, label: "Yesterday" }; }
    case "7d": { const d = new Date(); d.setDate(d.getDate() - 6); return { start: toISO(d), end: today, label: "Last 7 Days" }; }
    case "30d": { const d = new Date(); d.setDate(d.getDate() - 29); return { start: toISO(d), end: today, label: "Last 30 Days" }; }
    case "this_month": { const s = new Date(now.getFullYear(), now.getMonth(), 1); return { start: toISO(s), end: today, label: "This Month" }; }
    case "last_month": { const s = new Date(now.getFullYear(), now.getMonth() - 1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); return { start: toISO(s), end: toISO(e), label: "Last Month" }; }
    case "this_year": { const s = new Date(now.getFullYear(), 0, 1); return { start: toISO(s), end: today, label: "This Year" }; }
    default: return { start: today, end: today, label: "Today" };
  }
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_year", label: "This Year" },
];

const STATUS_COLORS_MAP = {
  "NEW LEAD": "#3b82f6", "NEW": "#3b82f6", "CONTACTED": "#06b6d4",
  "RCB WHATSAPP": "#0891b2", "FOLLOW_UP": "#8b5cf6", "FOLLOW UP": "#8b5cf6",
  "INTERESTED": "#f59e0b", "HOT LEADS": "#f97316", "PAYMENT LINK SENT": "#eab308",
  "PAID": "#10b981", "CONVERTED": "#10b981", "CAR_FINALIZED": "#059669",
  "CAR_PURCHASED": "#059669", "RNR": "#ef4444", "CANCELLED": "#64748b",
  "LOST": "#94a3b8", "CALLBACK": "#a855f7", "NOT INTERESTED": "#6b7280",
};
const PIE_FALLBACK = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4","#f97316","#64748b","#a3e635"];
const PKG_COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4","#f97316","#14b8a6","#a855f7"];

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className="p-1.5 bg-slate-100 rounded-md"><Icon className="w-4 h-4 text-slate-600" strokeWidth={2} /></div>
      <div>
        <h3 className="text-sm font-semibold text-slate-800 tracking-tight">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-400">{subtitle}</p>}
      </div>
    </div>
  );
}

function PrimaryKpi({ icon: Icon, label, value, sub, accent = "blue" }) {
  const accents = { blue: "text-blue-600", emerald: "text-emerald-600", violet: "text-violet-600", amber: "text-amber-600" };
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5 hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        <Icon className={`w-4 h-4 ${accents[accent]}`} strokeWidth={2} />
      </div>
      <div className="text-3xl font-bold text-slate-900 tracking-tight leading-none">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1.5">{sub}</div>}
    </div>
  );
}

function ConversionCard({ label, count, pool, pct, accent = "amber" }) {
  const accents = {
    amber: { ring: "border-amber-200", bg: "bg-amber-50", badge: "bg-amber-100 text-amber-700", text: "text-amber-700" },
    violet: { ring: "border-violet-200", bg: "bg-violet-50", badge: "bg-violet-100 text-violet-700", text: "text-violet-700" },
    emerald: { ring: "border-emerald-200", bg: "bg-emerald-50", badge: "bg-emerald-100 text-emerald-700", text: "text-emerald-700" },
  };
  const a = accents[accent] || accents.amber;
  return (
    <div className={`rounded-xl border ${a.ring} ${a.bg} p-4`}>
      <div className="text-xs font-semibold text-slate-600 mb-2">{label}</div>
      <div className="flex items-end gap-2">
        <span className={`text-2xl font-bold ${a.text}`}>{count}</span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${a.badge}`}>{pct}%</span>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">from {fmt(pool)} in pipeline</div>
    </div>
  );
}

function ROIChart({ data, nameAccessor }) {
  const chartData = (data || []).filter(d => d.revenue > 0 || d.meta_leads > 0).slice(0, 8).map(d => ({
    name: nameAccessor(d), Revenue: d.revenue || 0, "Ad Spend": d.estimated_spend || 0,
  }));
  if (!chartData.length) return <p className="text-center text-sm text-slate-400 py-8">No ROI data available</p>;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={chartData} margin={{ left: -5, right: 10, top: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtK(v)} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} formatter={(val) => fmtK(val)} />
        <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} iconType="circle" />
        <Bar dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={28} />
        <Bar dataKey="Ad Spend" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={28} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function LeadsByStatusModal({ isOpen, onClose, filterType, filterId, filterLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (isOpen && filterId) {
      setLoading(true);
      const params = filterType === "employee" ? { employee_id: filterId } : { city: filterId };
      dashboardApi.getLeadsByStatusCounts(params)
        .then(res => setData(res.data)).catch(() => toast.error("Failed to load lead status counts"))
        .finally(() => setLoading(false));
    }
  }, [isOpen, filterId, filterType]);
  const statuses = data?.statuses || [];
  const pieData = statuses.map((s, i) => ({ name: s.status, value: s.count, fill: STATUS_COLORS_MAP[s.status] || PIE_FALLBACK[i % PIE_FALLBACK.length] }));
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[520px] p-0 overflow-hidden bg-white" data-testid="leads-status-modal">
        <DialogTitle className="sr-only">{filterLabel} - Leads by Status</DialogTitle>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div><h3 className="text-base font-semibold text-slate-900">{filterLabel || "Leads"}</h3><p className="text-xs text-slate-400 mt-0.5">{data?.total_leads || 0} total leads</p></div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="close-status-modal"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
          : statuses.length > 0 ? (
            <div className="flex gap-5">
              <div className="flex-shrink-0"><ResponsiveContainer width={160} height={160}><PieChart><Pie data={pieData} cx="50%" cy="50%" outerRadius={70} innerRadius={35} dataKey="value" strokeWidth={2} stroke="#fff">{pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} /></PieChart></ResponsiveContainer></div>
              <div className="flex-1 space-y-1.5 overflow-y-auto max-h-[300px]">
                {statuses.map((s, i) => (
                  <div key={s.status} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-slate-50">
                    <div className="flex items-center gap-2.5"><div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[s.status] || PIE_FALLBACK[i % PIE_FALLBACK.length] }} /><span className="text-sm text-slate-700">{s.status}</span></div>
                    <span className="text-sm font-semibold text-slate-900 tabular-nums">{fmt(s.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="text-center py-10 text-slate-400"><Users className="w-8 h-8 mx-auto text-slate-300 mb-2" /><p className="text-sm">No leads found</p></div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PerformanceTable({ data, type, onViewLeads }) {
  const isExec = type === "exec";
  if (!data || data.length === 0) return <div className="text-center py-8 text-slate-400"><BarChart3 className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">No {isExec ? "executive" : "city"} data</p></div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid={`${type}-table`}>
        <thead><tr className="border-b border-slate-200 bg-slate-50/50">
          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">{isExec ? "EXECUTIVE" : "CITY"}</th>
          {["Leads","Sales","U.Sales","Conv %","Revenue"].map(h => <th key={h} className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
          <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-amber-600">RNR→Sale %</th>
          <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-violet-600">F/U→Sale %</th>
          <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Avg Sale Time</th>
          <th className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
        </tr></thead>
        <tbody>
          {data.map((row, i) => {
            const name = isExec ? row.employee : row.city;
            const id = isExec ? row.employee_id : row.city;
            return (
              <tr key={i} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors ${i === 0 ? "bg-blue-50/20" : ""}`}>
                <td className="px-4 py-2.5 text-[13px] font-medium text-slate-800">{name}</td>
                <td className="px-3 py-2.5 text-[13px] text-right text-slate-600 tabular-nums">{fmt(row.leads)}</td>
                <td className="px-3 py-2.5 text-[13px] text-right tabular-nums font-semibold text-emerald-600">{fmt(row.sales)}</td>
                <td className="px-3 py-2.5 text-[13px] text-right tabular-nums text-blue-600 font-medium">{fmt(row.unique_sales)}</td>
                <td className="px-3 py-2.5 text-[13px] text-right text-slate-600 tabular-nums">{row.conversion}%</td>
                <td className="px-3 py-2.5 text-[13px] text-right tabular-nums font-semibold text-slate-900">{fmtK(row.revenue)}</td>
                <td className="px-3 py-2.5 text-[13px] text-right tabular-nums"><span className="text-amber-600 font-medium">{row.rnr_to_sale_pct || 0}%</span>{row.rnr_to_sale > 0 && <span className="text-[10px] text-slate-400 ml-1">({row.rnr_to_sale})</span>}</td>
                <td className="px-3 py-2.5 text-[13px] text-right tabular-nums"><span className="text-violet-600 font-medium">{row.followup_to_sale_pct || 0}%</span>{row.followup_to_sale > 0 && <span className="text-[10px] text-slate-400 ml-1">({row.followup_to_sale})</span>}</td>
                <td className="px-3 py-2.5 text-[13px] text-right tabular-nums text-slate-600">{row.avg_sale_time_display || "-"}</td>
                <td className="px-3 py-2.5 text-center"><button onClick={() => onViewLeads(id, name)} className="p-1.5 rounded-md hover:bg-blue-100 text-blue-500 hover:text-blue-700 transition-colors" data-testid={`view-status-${type}-${i}`}><Eye className="w-4 h-4" /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PackageBreakdown({ data }) {
  const [view, setView] = useState("overall");
  const [selected, setSelected] = useState("");
  if (!data) return null;
  const overallList = data.overall || [];
  const employeeMap = data.by_employee || {};
  const cityMap = data.by_city || {};
  let displayData = overallList;
  let groupKeys = [];
  if (view === "employee") { groupKeys = Object.keys(employeeMap).sort(); displayData = selected && employeeMap[selected] ? employeeMap[selected] : (groupKeys.length ? employeeMap[groupKeys[0]] || [] : []); }
  else if (view === "city") { groupKeys = Object.keys(cityMap).sort(); displayData = selected && cityMap[selected] ? cityMap[selected] : (groupKeys.length ? cityMap[groupKeys[0]] || [] : []); }
  const activeKey = view === "overall" ? null : (selected || groupKeys[0] || "");
  const totalCount = displayData.reduce((s, d) => s + d.count, 0);
  const totalRev = displayData.reduce((s, d) => s + d.revenue, 0);
  const pieData = displayData.map((d, i) => ({ name: d.package, value: d.count, fill: PKG_COLORS[i % PKG_COLORS.length] }));
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="package-breakdown">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <SectionHeader icon={Package} title="Package Breakdown" subtitle={`${totalCount} inspections · ${fmtK(totalRev)} revenue`} />
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="pkg-view-toggle">
          {[{ key: "overall", label: "Overall" }, { key: "employee", label: "By Employee" }, { key: "city", label: "By City" }].map(opt => (
            <button key={opt.key} onClick={() => { setView(opt.key); setSelected(""); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === opt.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`pkg-radio-${opt.key}`}>{opt.label}</button>
          ))}
        </div>
      </div>
      <div className="p-5">
        {view !== "overall" && groupKeys.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4">{groupKeys.map(k => <button key={k} onClick={() => setSelected(k)} className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${activeKey === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>{k}</button>)}</div>}
        {displayData.length > 0 ? (
          <div className="flex gap-6 items-start">
            <div className="flex-shrink-0"><ResponsiveContainer width={150} height={150}><PieChart><Pie data={pieData} cx="50%" cy="50%" outerRadius={65} innerRadius={30} dataKey="value" strokeWidth={2} stroke="#fff">{pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} /></PieChart></ResponsiveContainer></div>
            <div className="flex-1 overflow-x-auto">
              <table className="w-full"><thead><tr className="border-b border-slate-200">{["Package","Count","Revenue","Share"].map((h,i) => <th key={i} className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${i===0?"text-left":"text-right"}`}>{h}</th>)}</tr></thead>
              <tbody>{displayData.map((d, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-[13px] font-medium text-slate-800 flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: PKG_COLORS[i % PKG_COLORS.length] }} />{d.package}</td>
                  <td className="px-3 py-2 text-[13px] text-right tabular-nums text-slate-700 font-semibold">{fmt(d.count)}</td>
                  <td className="px-3 py-2 text-[13px] text-right tabular-nums text-slate-600">{fmtK(d.revenue)}</td>
                  <td className="px-3 py-2 text-[13px] text-right tabular-nums text-slate-500">{totalCount > 0 ? Math.round(d.count / totalCount * 100) : 0}%</td>
                </tr>
              ))}</tbody></table>
            </div>
          </div>
        ) : <div className="text-center py-8 text-slate-400"><Package className="w-8 h-8 mx-auto text-slate-300 mb-2" /><p className="text-sm">No package data</p></div>}
      </div>
    </div>
  );
}

function PaymentLinkHealth({ data }) {
  if (!data || !data.total) return null;
  const segments = [
    { key: "paid", label: "Paid", count: data.paid?.count || 0, amount: data.paid?.amount || 0, color: "#10b981", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
    { key: "active", label: "Active", count: data.active?.count || 0, amount: data.active?.amount || 0, color: "#3b82f6", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700" },
    { key: "expired", label: "Expired", count: data.expired?.count || 0, amount: data.expired?.amount || 0, color: "#f59e0b", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
    { key: "failed", label: "Failed", count: data.failed?.count || 0, amount: data.failed?.amount || 0, color: "#ef4444", bg: "bg-red-50", border: "border-red-200", text: "text-red-700" },
  ];
  const total = data.total || 1;
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="payment-link-health">
      <div className="flex items-center justify-between mb-4">
        <SectionHeader icon={Link2} title="Payment Links Health" subtitle={`${fmt(data.total)} links sent`} />
        <div className="flex items-center gap-1.5" data-testid="pl-health-conversion-rate">
          <span className="text-2xl font-bold text-emerald-600 tracking-tight">{data.conversion_rate}%</span>
          <span className="text-[11px] text-slate-400 font-medium">converted</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {segments.map(s => (
          <div key={s.key} className={`${s.bg} border ${s.border} rounded-lg p-3.5`} data-testid={`pl-health-${s.key}`}>
            <div className={`text-[10px] font-semibold uppercase tracking-wider ${s.text} mb-1`}>{s.label}</div>
            <div className="text-xl font-bold text-slate-800 tracking-tight">{fmt(s.count)}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{fmtK(s.amount)}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden flex">
          {segments.map(s => s.count > 0 ? (
            <div key={s.key} className="h-full transition-all duration-500" style={{ width: `${(s.count / total * 100)}%`, backgroundColor: s.color }} title={`${s.label}: ${s.count}`} />
          ) : null)}
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-2">
          {segments.map(s => (
            <div key={s.key} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-[10px] text-slate-500">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HourlyDistribution({ data }) {
  const [view, setView] = useState("overall");
  const [selected, setSelected] = useState("");
  if (!data) return null;
  const overallArr = data.overall || [];
  const empMap = data.by_employee || {};
  const cityMap = data.by_city || {};

  let chartSource = overallArr;
  let groupKeys = [];
  if (view === "employee") { groupKeys = Object.keys(empMap).sort(); chartSource = selected && empMap[selected] ? empMap[selected] : (groupKeys.length ? empMap[groupKeys[0]] || [] : []); }
  else if (view === "city") { groupKeys = Object.keys(cityMap).sort(); chartSource = selected && cityMap[selected] ? cityMap[selected] : (groupKeys.length ? cityMap[groupKeys[0]] || [] : []); }
  const activeKey = view === "overall" ? null : (selected || groupKeys[0] || "");

  const hasActivity = chartSource.some(d => d.leads > 0 || d.sales > 0);
  const chartData = hasActivity ? chartSource.filter(d => d.leads > 0 || d.sales > 0 || (d.hour >= 8 && d.hour <= 21)) : chartSource.filter(d => d.hour >= 8 && d.hour <= 21);

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="hourly-distribution">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <SectionHeader icon={Clock} title="Hourly Lead & Sales Distribution" subtitle="Leads and sales volume by hour of day" />
        <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5 w-fit">
          {[["overall","Overall"],["employee","By Employee"],["city","By City"]].map(([k,l]) =>
            <button key={k} onClick={() => { setView(k); setSelected(""); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${view === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
          )}
        </div>
      </div>
      {view !== "overall" && groupKeys.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {groupKeys.map(k => <button key={k} onClick={() => setSelected(k)}
            className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${activeKey === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>{k}</button>)}
        </div>
      )}
      {hasActivity ? (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ left: -5, right: 10, top: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="leads" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <YAxis yAxisId="sales" orientation="right" tick={{ fill: "#10b981", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
            <Bar yAxisId="leads" dataKey="leads" name="Leads" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={32} />
            <Line yAxisId="sales" type="monotone" dataKey="sales" name="Sales" stroke="#10b981" strokeWidth={3} dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
          </ComposedChart>
        </ResponsiveContainer>
      ) : <div className="text-center py-8 text-slate-400"><Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" /><p className="text-sm">No hourly activity data</p></div>}
    </div>
  );
}

function AdPerformanceAnalytics({ data }) {
  if (!data || data.length === 0) return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="ad-performance">
      <SectionHeader icon={Megaphone} title="Ad Performance Analytics" subtitle="Campaign-level Meta Ads metrics" />
      <div className="text-center py-8 text-slate-400"><Megaphone className="w-8 h-8 mx-auto text-slate-300 mb-2" /><p className="text-sm">No ad performance data available</p></div>
    </div>
  );
  const totalSpend = data.reduce((s, d) => s + d.spend, 0);
  const totalClicks = data.reduce((s, d) => s + d.clicks, 0);
  const totalImpr = data.reduce((s, d) => s + d.impressions, 0);
  const totalLeads = data.reduce((s, d) => s + d.messaging_leads, 0);
  const totalReach = data.reduce((s, d) => s + (d.reach || 0), 0);
  const chartData = data.slice(0, 8).map(d => ({ name: d.campaign.length > 15 ? d.campaign.slice(0, 15) + ".." : d.campaign, Spend: d.spend, Leads: d.messaging_leads }));
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="ad-performance">
      <div className="px-5 py-3.5 border-b border-slate-100"><SectionHeader icon={Megaphone} title="Ad Performance Analytics" subtitle={`${data.length} campaigns · ${fmtK(totalSpend)} total spend · ${fmt(totalLeads)} messaging leads`} /></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-slate-100">
        {[{ label: "Total Spend", value: fmtK(totalSpend), color: "text-slate-900" },{ label: "Total Clicks", value: fmt(totalClicks), color: "text-blue-600" },{ label: "Avg CPR", value: `₹${totalReach > 0 ? (totalSpend / totalReach).toFixed(2) : 0}`, color: "text-amber-600" },{ label: "Avg CPL", value: `₹${totalLeads > 0 ? Math.round(totalSpend / totalLeads) : 0}`, color: "text-rose-600" }].map((m, i) => (
          <div key={i} className="bg-slate-50 rounded-lg p-3"><div className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">{m.label}</div><div className={`text-lg font-bold tabular-nums ${m.color}`}>{m.value}</div></div>
        ))}
      </div>
      <div className="p-4 border-b border-slate-100">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} margin={{ left: -5, right: 10, top: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="spend" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => fmtK(v)} />
            <YAxis yAxisId="leads" orientation="right" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} iconType="circle" />
            <Bar yAxisId="spend" dataKey="Spend" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={24} />
            <Bar yAxisId="leads" dataKey="Leads" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="ad-campaign-table"><thead><tr className="border-b border-slate-200 bg-slate-50/50">
          {["Campaign","Ad Sets","Spend","Impressions","Clicks","CPR","CPC","Msg Leads","CPL"].map((h,i) => <th key={i} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${i===0?"text-left":"text-right"}`}>{h}</th>)}
        </tr></thead><tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
              <td className="px-3 py-2.5 text-[13px] font-medium text-slate-800 max-w-[200px] truncate">{row.campaign}</td>
              <td className="px-3 py-2.5 text-[13px] text-right text-slate-600 tabular-nums">{row.adsets}</td>
              <td className="px-3 py-2.5 text-[13px] text-right tabular-nums font-semibold text-rose-600">{fmtK(row.spend)}</td>
              <td className="px-3 py-2.5 text-[13px] text-right text-slate-600 tabular-nums">{fmt(row.impressions)}</td>
              <td className="px-3 py-2.5 text-[13px] text-right text-slate-600 tabular-nums">{fmt(row.clicks)}</td>
              <td className="px-3 py-2.5 text-[13px] text-right text-slate-600 tabular-nums">₹{row.reach > 0 ? (row.spend / row.reach).toFixed(1) : 0}</td>
              <td className="px-3 py-2.5 text-[13px] text-right text-slate-600 tabular-nums">₹{fmt(row.cpc)}</td>
              <td className="px-3 py-2.5 text-[13px] text-right tabular-nums font-semibold text-emerald-600">{fmt(row.messaging_leads)}</td>
              <td className="px-3 py-2.5 text-[13px] text-right tabular-nums text-slate-600">₹{fmt(row.cost_per_lead)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

function LeadsByStatus({ data, dateRange }) {
  const [activeTab, setActiveTab] = useState("overall");
  const [expanded, setExpanded] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch leads by status data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (dateRange?.start) params.start_date = dateRange.start;
        if (dateRange?.end) params.end_date = dateRange.end;
        const response = await dashboardApi.getLeadsByStatusSection(params);
        setStatusData(response.data);
      } catch (error) {
        console.error("Failed to fetch leads by status:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [dateRange]);

  const tabs = [
    { id: "overall", label: "Overall" },
    { id: "by_employee", label: "By Employee" },
    { id: "by_city", label: "By City" },
  ];

  if (loading || !statusData) {
    return (
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="leads-by-status">
        <div className="px-5 py-3.5 border-b border-slate-100">
          <SectionHeader icon={BarChart3} title="Leads by Status" subtitle="Loading..." />
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const renderOverall = () => {
    const overall = statusData.overall || [];
    const total = statusData.total_leads || 0;
    return (
      <div className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {overall.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-200/70">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[item.status] || "#94a3b8" }} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-slate-500 truncate">{item.status}</div>
                <div className="text-sm font-semibold text-slate-800">{fmt(item.count)}</div>
              </div>
              <div className="text-[11px] text-slate-400 font-medium">{total > 0 ? ((item.count / total) * 100).toFixed(1) : 0}%</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderByEmployee = () => {
    const employees = statusData.by_employee || [];
    const grandTotal = statusData.total_leads || 0;
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-slate-200 bg-slate-50/50">
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Employee</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">%</th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Top Statuses</th>
            <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-10"></th>
          </tr></thead>
          <tbody>
            {employees.map((row, i) => {
              const isOpen = expanded === `emp-${row.employee_id}`;
              const topStatuses = (row.statuses || []).slice(0, 3);
              return (
                <React.Fragment key={i}>
                  <tr className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 cursor-pointer transition-colors ${isOpen ? "bg-slate-50" : ""}`}
                    onClick={() => setExpanded(isOpen ? null : `emp-${row.employee_id}`)} data-testid={`leads-status-emp-row-${i}`}>
                    <td className="px-4 py-2.5 text-[13px] font-medium text-slate-800">{row.employee}</td>
                    <td className="px-4 py-2.5 text-right text-[13px] font-semibold text-slate-700 tabular-nums">{fmt(row.total)}</td>
                    <td className="px-4 py-2.5 text-right text-[13px] text-slate-500 tabular-nums">{grandTotal > 0 ? ((row.total / grandTotal) * 100).toFixed(1) : 0}%</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {topStatuses.map((st, j) => (
                          <span key={j} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[st.status] || "#94a3b8" }} />
                            {st.status} <span className="font-semibold">{st.count}</span>
                          </span>
                        ))}
                        {row.statuses?.length > 3 && <span className="text-[10px] text-slate-400">+{row.statuses.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-center"><Eye className={`w-4 h-4 transition-transform ${isOpen ? "text-blue-500 rotate-90" : "text-slate-400"}`} /></td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan={5} className="px-0 py-0">
                      <div className="bg-slate-50/80 border-t border-slate-200 px-6 py-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                          {(row.statuses || []).map((st, j) => (
                            <div key={j} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200/70">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[st.status] || "#94a3b8" }} />
                              <div className="min-w-0 flex-1">
                                <div className="text-[11px] text-slate-500 truncate">{st.status}</div>
                                <div className="text-sm font-semibold text-slate-800">{st.count}</div>
                              </div>
                              <div className="text-[10px] text-slate-400 font-medium">{row.total > 0 ? ((st.count / row.total) * 100).toFixed(0) : 0}%</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderByCity = () => {
    const cities = statusData.by_city || [];
    const grandTotal = statusData.total_leads || 0;
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-slate-200 bg-slate-50/50">
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">City</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">%</th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Top Statuses</th>
            <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-10"></th>
          </tr></thead>
          <tbody>
            {cities.map((row, i) => {
              const isOpen = expanded === `city-${row.city}`;
              const topStatuses = (row.statuses || []).slice(0, 3);
              return (
                <React.Fragment key={i}>
                  <tr className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 cursor-pointer transition-colors ${isOpen ? "bg-slate-50" : ""}`}
                    onClick={() => setExpanded(isOpen ? null : `city-${row.city}`)} data-testid={`leads-status-city-row-${i}`}>
                    <td className="px-4 py-2.5 text-[13px] font-medium text-slate-800">{row.city}</td>
                    <td className="px-4 py-2.5 text-right text-[13px] font-semibold text-slate-700 tabular-nums">{fmt(row.total)}</td>
                    <td className="px-4 py-2.5 text-right text-[13px] text-slate-500 tabular-nums">{grandTotal > 0 ? ((row.total / grandTotal) * 100).toFixed(1) : 0}%</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {topStatuses.map((st, j) => (
                          <span key={j} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[st.status] || "#94a3b8" }} />
                            {st.status} <span className="font-semibold">{st.count}</span>
                          </span>
                        ))}
                        {row.statuses?.length > 3 && <span className="text-[10px] text-slate-400">+{row.statuses.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-center"><Eye className={`w-4 h-4 transition-transform ${isOpen ? "text-blue-500 rotate-90" : "text-slate-400"}`} /></td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan={5} className="px-0 py-0">
                      <div className="bg-slate-50/80 border-t border-slate-200 px-6 py-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                          {(row.statuses || []).map((st, j) => (
                            <div key={j} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200/70">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[st.status] || "#94a3b8" }} />
                              <div className="min-w-0 flex-1">
                                <div className="text-[11px] text-slate-500 truncate">{st.status}</div>
                                <div className="text-sm font-semibold text-slate-800">{st.count}</div>
                              </div>
                              <div className="text-[10px] text-slate-400 font-medium">{row.total > 0 ? ((st.count / row.total) * 100).toFixed(0) : 0}%</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="leads-by-status">
      <div className="px-5 py-3.5 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <SectionHeader icon={BarChart3} title="Leads by Status" subtitle={`${fmt(statusData.total_leads)} total leads`} />
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setExpanded(null); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  activeTab === tab.id
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {activeTab === "overall" && renderOverall()}
      {activeTab === "by_employee" && renderByEmployee()}
      {activeTab === "by_city" && renderByCity()}
    </div>
  );
}

function LeadsByCity({ data }) {
  const [expanded, setExpanded] = useState(null);
  if (!data || data.length === 0) return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-100"><SectionHeader icon={MapPin} title="Leads by City" subtitle="Lead count and status distribution per city" /></div>
      <div className="text-center py-8 text-slate-400"><MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">No city data</p></div>
    </div>
  );
  const grandTotal = data.reduce((s, c) => s + c.total, 0);
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="leads-by-city">
      <div className="px-5 py-3.5 border-b border-slate-100">
        <SectionHeader icon={MapPin} title="Leads by City" subtitle={`${data.length} cities · ${fmt(grandTotal)} total leads · Click a city for status breakdown`} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-slate-200">
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">City</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Leads</th>
            <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">% Share</th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Top Statuses</th>
            <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-10"></th>
          </tr></thead>
          <tbody>
            {data.map((row, i) => {
              const isOpen = expanded === row.city;
              const statusEntries = Object.entries(row.statuses);
              const topStatuses = statusEntries.slice(0, 3);
              return (
                <React.Fragment key={i}>
                  <tr className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 cursor-pointer transition-colors ${isOpen ? "bg-slate-50" : ""}`}
                    onClick={() => setExpanded(isOpen ? null : row.city)} data-testid={`leads-city-row-${i}`}>
                    <td className="px-4 py-2.5 text-[13px] font-medium text-slate-800">{row.city}</td>
                    <td className="px-4 py-2.5 text-right text-[13px] font-semibold text-slate-700 tabular-nums">{fmt(row.total)}</td>
                    <td className="px-4 py-2.5 text-right text-[13px] text-slate-500 tabular-nums">{grandTotal > 0 ? ((row.total / grandTotal) * 100).toFixed(1) : 0}%</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {topStatuses.map(([st, ct], j) => (
                          <span key={j} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[st] || "#94a3b8" }} />
                            {st} <span className="font-semibold">{ct}</span>
                          </span>
                        ))}
                        {statusEntries.length > 3 && <span className="text-[10px] text-slate-400">+{statusEntries.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-center"><Eye className={`w-4 h-4 transition-transform ${isOpen ? "text-blue-500 rotate-90" : "text-slate-400"}`} /></td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan={5} className="px-0 py-0">
                      <div className="bg-slate-50/80 border-t border-slate-200 px-6 py-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                          {statusEntries.map(([st, ct], j) => (
                            <div key={j} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200/70">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[st] || "#94a3b8" }} />
                              <div className="min-w-0 flex-1">
                                <div className="text-[11px] text-slate-500 truncate">{st}</div>
                                <div className="text-sm font-semibold text-slate-800">{ct}</div>
                              </div>
                              <div className="text-[10px] text-slate-400 font-medium">{row.total > 0 ? ((ct / row.total) * 100).toFixed(0) : 0}%</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function StatusAgingAnalysis({ execData, cityData }) {
  const [view, setView] = useState("overall");
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchAging = useCallback(async (params = {}) => {
    setLoading(true);
    try { const res = await dashboardApi.getStatusAging(params); setData(res.data); }
    catch { toast.error("Failed to load status aging data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAging(); }, [fetchAging]);
  const handleViewChange = (v) => { setView(v); setSelected(""); if (v === "overall") fetchAging(); };
  const handleSelect = (key, type) => {
    setSelected(key);
    if (type === "employee") { const emp = execData?.find(e => e.employee === key); if (emp) fetchAging({ employee_id: emp.employee_id }); }
    else fetchAging({ city: key });
  };
  const groupKeys = view === "employee" ? (execData || []).map(e => e.employee) : view === "city" ? (cityData || []).map(c => c.city) : [];
  const activeKey = selected || (groupKeys.length ? groupKeys[0] : "");
  const aging = data?.aging || [];
  useEffect(() => {
    if (view === "employee" && groupKeys.length && !selected) { const emp = execData?.find(e => e.employee === groupKeys[0]); if (emp) fetchAging({ employee_id: emp.employee_id }); }
    else if (view === "city" && groupKeys.length && !selected) fetchAging({ city: groupKeys[0] });
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const agingColor = (days) => { if (days > 14) return "text-red-600 bg-red-50"; if (days > 7) return "text-orange-600 bg-orange-50"; if (days > 3) return "text-amber-600 bg-amber-50"; return "text-emerald-600 bg-emerald-50"; };
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="status-aging">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <SectionHeader icon={Hourglass} title="Status Aging Analysis" subtitle={`${data?.total_leads || 0} leads · How long leads sit in each status`} />
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="aging-view-toggle">
          {[{ key: "overall", label: "Overall" },{ key: "employee", label: "By Employee" },{ key: "city", label: "By City" }].map(opt => (
            <button key={opt.key} onClick={() => handleViewChange(opt.key)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === opt.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid={`aging-radio-${opt.key}`}>{opt.label}</button>
          ))}
        </div>
      </div>
      <div className="p-5">
        {view !== "overall" && groupKeys.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4">{groupKeys.map(k => <button key={k} onClick={() => handleSelect(k, view)} className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${activeKey === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>{k}</button>)}</div>}
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
        : aging.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="aging-table"><thead><tr className="border-b border-slate-200 bg-slate-50/50">
              {["Status","Count","Avg Days","Min Days","Max Days"].map((h,i) => <th key={i} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${i===0?"text-left":"text-right"}`}>{h}</th>)}
            </tr></thead><tbody>
              {aging.map((row, i) => (
                <tr key={i} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 ${row.is_terminal ? "opacity-50" : ""}`}>
                  <td className="px-3 py-2.5 text-[13px] font-medium text-slate-800 flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS_MAP[row.status] || "#94a3b8" }} />{row.status}{row.is_terminal && <span className="text-[10px] text-slate-400 ml-1">(closed)</span>}</td>
                  <td className="px-3 py-2.5 text-[13px] text-right tabular-nums font-semibold text-slate-700">{fmt(row.count)}</td>
                  <td className="px-3 py-2.5 text-right"><span className={`text-[13px] tabular-nums font-semibold px-2 py-0.5 rounded-md ${row.is_terminal ? "text-slate-500" : agingColor(row.avg_days)}`}>{row.avg_days}d</span></td>
                  <td className="px-3 py-2.5 text-[13px] text-right tabular-nums text-slate-500">{row.min_days}d</td>
                  <td className="px-3 py-2.5 text-[13px] text-right tabular-nums text-slate-500">{row.max_days}d</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        ) : <div className="text-center py-8 text-slate-400"><Hourglass className="w-8 h-8 mx-auto text-slate-300 mb-2" /><p className="text-sm">No aging data</p></div>}
      </div>
    </div>
  );
}

// ── Main Component ──
export default function TodayView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [modal, setModal] = useState(null);
  const [activePreset, setActivePreset] = useState("today");
  const [dateRange, setDateRange] = useState(() => getPresetRange("today"));
  // Meta token / refresh state
  const [refreshing, setRefreshing] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [newToken, setNewToken] = useState("");
  const [tokenLoading, setTokenLoading] = useState(false);
  const ciRef = useRef(null);

  const fetchData = useCallback(async (range) => {
    setLoading(true);
    try {
      const tz_offset = -new Date().getTimezoneOffset(); // Browser timezone offset in minutes from UTC
      const params = range.start === range.end
        ? { date: range.start, timezone_offset: tz_offset }
        : { start_date: range.start, end_date: range.end, timezone_offset: tz_offset };
      const res = await dashboardApi.getTodayDashboard(params);
      setData(res.data);
      setLastRefresh(new Date());
      // Check if Meta token expired (backend will flag this)
      if (res.data?.ad_metrics?.token_expired) {
        toast.warning("Meta Ads token expired — click 'Sync Meta Ads' to reconnect", { duration: 6000 });
      }
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(dateRange);
    const interval = setInterval(() => fetchData(dateRange), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData, dateRange]);

  const handlePreset = (key) => {
    setActivePreset(key);
    setDateRange(getPresetRange(key));
  };

  const handleCustomDate = (start, end) => {
    setActivePreset("custom");
    setDateRange({ start, end: end || start, label: start === end ? start : `${start} to ${end}` });
  };

  // Refresh dashboard data only (leads, sales, etc.)
  const handleRefreshData = async () => {
    setRefreshing(true);
    try {
      await fetchData(dateRange);
      toast.success("Dashboard data refreshed!");
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  // Sync Meta Ads: check token → sync from Meta → refresh dashboard
  const handleSyncMeta = async () => {
    setRefreshing(true);
    try {
      // Step 1: Check Meta token status
      let tokenValid = false;
      try {
        const tokenRes = await metaAdsApi.getTokenInfo();
        const info = tokenRes.data;
        setTokenInfo(info);
        tokenValid = info.is_valid;
        if (!tokenValid) {
          toast.info("Token expired - please reconnect to Facebook");
          setShowTokenModal(true);
          setRefreshing(false);
          return;
        }
      } catch {
        toast.error("Could not check token status");
        setRefreshing(false);
        return;
      }

      // Step 2: Sync latest data from Meta
      if (tokenValid) {
        try {
          const syncRes = await metaAdsApi.forceSync();
          if (syncRes.data?.success) {
            toast.success("Ad data synced from Meta!");
          }
        } catch {
          toast.error("Meta sync failed");
        }
      }

      // Step 3: Refresh dashboard to show updated ad data
      await fetchData(dateRange);
    } catch {
      toast.error("Sync failed");
    } finally {
      setRefreshing(false);
    }
  };

  // Save new Meta token
  const handleUpdateToken = async () => {
    if (!newToken.trim()) { toast.error("Please enter a valid token"); return; }
    setTokenLoading(true);
    try {
      const result = await metaAdsApi.updateToken(newToken.trim());
      if (result.data.success) {
        toast.success("Token updated successfully!");
        setShowTokenModal(false);
        setNewToken("");
        try { const tr = await metaAdsApi.getTokenInfo(); setTokenInfo(tr.data); } catch {}
        // Auto-sync + refresh after connecting
        setRefreshing(true);
        try { await metaAdsApi.forceSync(); } catch {}
        await fetchData(dateRange);
        setRefreshing(false);
        toast.success("Ad data synced and dashboard refreshed!");
      } else {
        toast.error(result.data.error || "Invalid token");
      }
    } catch {
      toast.error("Failed to update token");
    } finally {
      setTokenLoading(false);
    }
  };

  if (loading && !data) return <div className="flex items-center justify-center h-[60vh]" data-testid="today-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  const isRefreshing = loading && !!data;

  const d = data || {};
  const isToday = activePreset === "today";
  const dateLabel = dateRange.label;

  const roiCityCols = [
    { label: "City", accessor: r => r.city },
    { label: "Meta Leads", accessor: r => r.meta_leads, format: fmt },
    { label: "Sales", accessor: r => r.meta_sales, format: fmt, highlight: "font-semibold text-emerald-600" },
    { label: "Conv %", accessor: r => r.conversion, format: v => `${v}%` },
    { label: "Revenue", accessor: r => r.revenue, format: fmtK },
    { label: "Ad Spend", accessor: r => r.estimated_spend, format: fmtK, highlight: "text-rose-500" },
    { label: "ROI", accessor: r => r.roi, format: v => `${v > 0 ? "+" : ""}${v}%`, highlight: "font-bold" },
  ];

  return (
    <div className="space-y-6 relative" data-testid="today-view">

      {/* Loading overlay for data refresh */}
      {isRefreshing && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-40 flex items-start justify-center pt-48 rounded-xl" data-testid="dashboard-loading-overlay">
          <div className="flex flex-col items-center gap-3 bg-white rounded-2xl px-8 py-6 shadow-lg border border-slate-100">
            <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
            <span className="text-sm font-medium text-slate-600">Updating dashboard...</span>
          </div>
        </div>
      )}
      {/* ── HEADER WITH DATE SELECTION ── */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Dashboard</h2>
            <p className="text-xs text-slate-400 mt-0.5">{d.date || dateRange.start} &middot; {isToday ? "Real-time" : dateLabel} business metrics</p>
          </div>
          <div className="flex items-center gap-2">
            {lastRefresh && <span className="text-xs text-slate-400 flex items-center gap-1.5 hidden md:flex"><Clock className="w-3.5 h-3.5" />{lastRefresh.toLocaleTimeString()}</span>}
            <button onClick={handleRefreshData} disabled={loading || refreshing}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg transition-colors disabled:opacity-50 shadow-sm font-medium text-xs"
              data-testid="refresh-data-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${(loading || refreshing) ? "animate-spin" : ""}`} />
              Refresh Data
            </button>
            <button onClick={handleSyncMeta} disabled={loading || refreshing}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm font-medium text-xs"
              data-testid="sync-meta-btn">
              <Megaphone className={`w-3.5 h-3.5 ${refreshing ? "animate-pulse" : ""}`} />
              Sync Meta Ads
            </button>
            <button onClick={() => ciRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="flex items-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors shadow-sm font-medium text-xs"
              data-testid="ai-analytics-btn">
              <Brain className="w-3.5 h-3.5" />
              AI Analytics
            </button>
          </div>
        </div>
        {/* Date controls row */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Presets */}
          <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm" data-testid="date-presets">
            {PRESETS.map(p => (
              <button key={p.key} onClick={() => handlePreset(p.key)}
                className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-colors whitespace-nowrap ${activePreset === p.key ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
                data-testid={`preset-${p.key}`}>{p.label}</button>
            ))}
          </div>
          {/* Divider */}
          <div className="w-px h-6 bg-slate-200 hidden sm:block" />
          {/* Custom date range */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <input type="date" value={dateRange.start}
                onChange={(e) => e.target.value && handleCustomDate(e.target.value, dateRange.end >= e.target.value ? dateRange.end : e.target.value)}
                max={todayStr()}
                className="pl-7 pr-2 py-1.5 text-[11px] border border-slate-200 rounded-lg bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-[130px]"
                data-testid="date-start" />
              <Calendar className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <span className="text-xs text-slate-400">to</span>
            <div className="relative">
              <input type="date" value={dateRange.end}
                onChange={(e) => e.target.value && handleCustomDate(dateRange.start <= e.target.value ? dateRange.start : e.target.value, e.target.value)}
                min={dateRange.start} max={todayStr()}
                className="pl-7 pr-2 py-1.5 text-[11px] border border-slate-200 rounded-lg bg-white text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-[130px]"
                data-testid="date-end" />
              <Calendar className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* ── ROW 1: PRIMARY KPIs ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="today-kpis">
        <PrimaryKpi icon={IndianRupee} label="Revenue" value={fmtK(d.total_revenue)} sub={`${fmt(d.unique_customers)} customers`} accent="amber" />
        <PrimaryKpi icon={TrendingUp} label="Sales" value={fmt(d.total_sales)} sub={`from ${fmt(d.total_leads)} leads`} accent="emerald" />
        <PrimaryKpi icon={Target} label="Total Leads" value={fmt(d.total_leads)} sub={d.payment_links_sent ? `${d.payment_links_sent} payment link${d.payment_links_sent > 1 ? 's' : ''} sent` : undefined} accent="blue" />
        <PrimaryKpi icon={Users} label="Conversion" value={`${d.conversion_pct || 0}%`} sub={`Ad spend: ${fmtK(d.ad_metrics?.total_spend)}`} accent="violet" />
      </div>

      {/* ── ROW 2: CONVERSION METRICS ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ConversionCard label="RNR to Sale" count={d.rnr_to_sale?.count || 0} pool={d.rnr_to_sale?.total_rnr_pool || 0} pct={d.rnr_to_sale?.pct || 0} accent="amber" />
        <ConversionCard label="Follow-up to Sale" count={d.followup_to_sale?.count || 0} pool={d.followup_to_sale?.total_followup_pool || 0} pct={d.followup_to_sale?.pct || 0} accent="violet" />
        <ConversionCard label="Same-Day Conversions" count={d.todays_lead_sales || 0} pool={d.total_leads || 0} pct={d.total_leads > 0 ? ((d.todays_lead_sales || 0) / d.total_leads * 100).toFixed(1) : 0} accent="emerald" />
      </div>

      {/* ── ROW 2b: PAYMENT LINKS HEALTH ── */}
      {d.payment_link_health && d.payment_link_health.total > 0 && <PaymentLinkHealth data={d.payment_link_health} />}

      {/* ── ROW 3: SALES BY EXECUTIVE ── */}
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100"><SectionHeader icon={UserCheck} title="Sales by Executive" subtitle="Performance, unique sales, RNR/Follow-up conversions, and average sale time" /></div>
        <PerformanceTable data={d.sales_by_exec} type="exec" onViewLeads={(id, name) => setModal({ type: "employee", id, label: name })} />
      </div>

      {/* ── ROW 4: SALES BY CITY ── */}
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100"><SectionHeader icon={MapPin} title="Sales by City" subtitle="Performance, unique sales, RNR/Follow-up conversions, and average sale time by location" /></div>
        <PerformanceTable data={d.sales_by_city} type="city" onViewLeads={(id, name) => setModal({ type: "city", id, label: name })} />
      </div>

      {/* ── ROW 4b: LEADS BY STATUS (with Overall, By Employee, By City tabs) ── */}
      <LeadsByStatus data={d.leads_by_city} dateRange={dateRange} />

      {/* ── ROW 5: PACKAGE BREAKDOWN ── */}
      <PackageBreakdown data={d.package_breakdown} />

      {/* ── ROW 6: HOURLY DISTRIBUTION ── */}
      <HourlyDistribution data={d.hourly_distribution} />

      {/* ── ROW 7: META ADS SUMMARY ── */}
      <div className="bg-slate-900 rounded-xl p-5" data-testid="meta-ads-summary">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Activity className="w-4 h-4 text-slate-400" /><h3 className="text-sm font-semibold text-white tracking-tight">Meta Ads Summary</h3></div>
          <div className="flex items-center gap-2">
            {d.ad_metrics?.token_expired && <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-medium">Token Expired</span>}
            {d.ad_metrics?.data_source === "live" && <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-medium">Live</span>}
            {d.ad_metrics?.data_source === "cache" && !d.ad_metrics?.token_expired && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-medium">Cached</span>}
            <span className="text-xs text-slate-500">{dateLabel}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: `${dateLabel} Ad Spend`, value: fmtK(d.ad_metrics?.total_spend), color: "text-white" },
            { label: "Meta Leads", value: fmt(d.ad_metrics?.total_meta_leads), color: "text-blue-400" },
            { label: "Meta Sales", value: fmt(d.ad_metrics?.total_meta_sales), color: "text-emerald-400" },
            { label: "Meta Conv %", value: `${d.ad_metrics?.meta_conversion || 0}%`, color: "text-amber-400" },
            { label: "Cost / Lead", value: `₹${fmt(d.ad_metrics?.cost_per_lead || 0)}`, color: "text-rose-400" },
            { label: "Cost / Sale", value: `₹${fmt(d.ad_metrics?.cost_per_sale || 0)}`, color: "text-violet-400" },
          ].map((m, i) => (
            <div key={i} className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/40">
              <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">{m.label}</div>
              <div className={`text-xl font-bold tabular-nums ${m.color}`}>{m.value}</div>
            </div>
          ))}
        </div>
        {d.ad_metrics?.token_expired && (
          <div className="mt-3 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            <span className="text-xs text-red-400">Meta token expired. Ad data may be stale.</span>
            <button onClick={handleSyncMeta} className="text-xs text-blue-400 hover:text-blue-300 font-semibold underline">Reconnect</button>
          </div>
        )}
      </div>

      {/* ── ROW 8: AD PERFORMANCE ANALYTICS ── */}
      <AdPerformanceAnalytics data={d.ad_performance_analytics} />

      {/* ── ROW 9: STATUS AGING ANALYSIS ── */}
      <StatusAgingAnalysis execData={d.sales_by_exec} cityData={d.sales_by_city} />

      {/* ── ROW 10: MARKETING ROI BY CITY ── */}
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100"><SectionHeader icon={MapPin} title="Marketing ROI by City" subtitle="Revenue vs ad spend by location" /></div>
        <div className="p-4"><ROIChart data={d.roi_by_city} nameAccessor={(r) => r.city?.length > 12 ? r.city.slice(0, 12) + ".." : r.city} /></div>
        {(d.roi_by_city || []).length > 0 && <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-slate-200">
          {roiCityCols.map((col, i) => <th key={i} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${i === 0 ? "text-left" : "text-right"}`}>{col.label}</th>)}
        </tr></thead><tbody>
          {(d.roi_by_city || []).slice(0, 8).map((row, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
              {roiCityCols.map((col, j) => { const val = col.accessor(row); return <td key={j} className={`px-3 py-2.5 text-[13px] ${j === 0 ? "text-left font-medium text-slate-800" : "text-right text-slate-600 tabular-nums"} ${col.highlight && val > 0 ? col.highlight : ""}`}>{col.format ? col.format(val) : val}</td>; })}
            </tr>
          ))}
        </tbody></table></div>}
      </div>

      {/* ── ROW 11: CONVERSION INTELLIGENCE (AI) ── */}
      <div ref={ciRef}>
        <ConversionIntelligence />
      </div>

      {/* Modal */}
      <LeadsByStatusModal isOpen={!!modal} onClose={() => setModal(null)} filterType={modal?.type} filterId={modal?.id} filterLabel={modal?.label} />

      {/* Connect to Facebook Ads Modal */}
      <Dialog open={showTokenModal} onOpenChange={setShowTokenModal}>
        <DialogContent className="sm:max-w-[550px]" data-testid="meta-token-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-600" />
              Connect to Facebook Ads
            </DialogTitle>
            <DialogDescription>
              Get a new access token in 3 simple steps
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {tokenInfo && (
              <div className={`p-3 rounded-lg border ${tokenInfo.is_valid ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-center gap-2">
                  <Shield className={`h-4 w-4 ${tokenInfo.is_valid ? "text-emerald-600" : "text-red-600"}`} />
                  <span className="font-medium text-sm">
                    {tokenInfo.is_valid ? `Connected (${tokenInfo.expires_in_days} days left)` : "Token Expired - Follow steps below"}
                  </span>
                </div>
              </div>
            )}
            <div className="space-y-3">
              <div className="flex gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <div className="flex-1">
                  <p className="font-medium text-sm text-gray-800">Click the button below to open Facebook</p>
                  <a href="https://developers.facebook.com/tools/explorer/?app_id=759383146339304&method=GET&path=me&version=v21.0"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                    <ExternalLink className="h-4 w-4" /> Open Graph API Explorer
                  </a>
                </div>
              </div>
              <div className="flex gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex-shrink-0 w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <div className="flex-1">
                  <p className="font-medium text-sm text-gray-800">Add these permissions & click "Generate Access Token"</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-xs font-mono">ads_read</span>
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-xs font-mono">ads_management</span>
                    <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-xs font-mono">business_management</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Click "Permissions" dropdown → search & add each one → click "Generate"</p>
                </div>
              </div>
              <div className="flex gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                <div className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <div className="flex-1">
                  <p className="font-medium text-sm text-gray-800">Copy the token & paste below</p>
                  <div className="flex gap-2 mt-2">
                    <Input placeholder="Paste your access token here..." value={newToken} onChange={(e) => setNewToken(e.target.value)}
                      className="flex-1 text-xs font-mono h-9" data-testid="dashboard-token-input" />
                    <Button onClick={handleUpdateToken} disabled={tokenLoading || !newToken.trim()} size="sm" className="bg-green-600 hover:bg-green-700">
                      {tokenLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-slate-100 p-3 rounded-lg text-xs text-gray-600 flex items-start gap-2">
              <Info className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <div><span className="font-medium">Pro tip:</span> The token lasts ~60 days. After connecting, you won't need to do this again for 2 months!</div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
